import { createTheme } from '@mui/material/styles';
import type {} from '@mui/x-data-grid/themeAugmentation';

// 테마에 사용할 사용자 정의 색상 타입을 확장합니다.
declare module '@mui/material/styles' {
  interface Palette {
    // 사용자 정의 색상 추가
    contentBlock: Palette['primary'] & { background?: string };
    fieldText: Palette['primary'] & { background?: string; title?: string; link?: string; linkHover?: string };
    selectButton: Palette['primary'] & { selected?: string; unselected?: string; selectedText?: string; unselectedText?: string };
    table: Palette['primary'] & { header?: string; headerText?: string; row?: string; rowText?: string; border?: string };
    tiptap: Palette['primary'] & { toolbar?: string; toolbarText?: string; readonly?: string; readonlyText?: string };
  }
  interface PaletteOptions {
    contentBlock?: PaletteOptions['primary'] & { background?: string };
    fieldText?: PaletteOptions['primary'] & { background?: string; title?: string; link?: string; linkHover?: string };
    selectButton: PaletteOptions['primary'] & { selected?: string; unselected?: string; selectedText?: string; unselectedText?: string };
    table: PaletteOptions['primary'] & { header?: string; headerText?: string; row?: string; rowText?: string; border?: string };
    tiptap?: PaletteOptions['primary'] & { toolbar?: string; toolbarText?: string; readonly?: string; readonlyText?: string };
  }
}

const baseTheme = createTheme({
  //   cssVariables: {
  //     colorSchemeSelector: 'data-toolpad-color-scheme' // 색상 테마 선택을 위한 CSS 변수
  //   },
  // colorSchemes: { light: true, dark: true },
  breakpoints: {
    values: {
      xs: 0, // xs 크기: 0px
      sm: 600, // sm 크기: 600px
      md: 600, // md 크기: 600px
      lg: 1200, // lg 크기: 1200px
      xl: 1536 // xl 크기: 1536px
    }
  },
  typography: {
    fontFamily: '"Noto Sans KR", "Meiryo UI", sans-serif',
    fontWeightRegular: 400, // 일반 텍스트의 폰트 두께
    fontWeightMedium: 500, // 중간 두께의 폰트
    fontWeightBold: 700, // 굵은 폰트
    body1: {
      lineHeight: 'normal' // 기본 본문 텍스트의 줄 간격
    },
    body2: {
      lineHeight: 'normal' // 본문 텍스트의 줄 간격
    },
    h1: {
      lineHeight: 'normal' // h1 헤더의 줄 간격
    },
    h2: {
      lineHeight: 'normal' // h2 헤더의 줄 간격
    },
    h3: {
      lineHeight: 'normal' // h3 헤더의 줄 간격
    },
    h4: {
      lineHeight: 'normal' // h4 헤더의 줄 간격
    },
    h5: {
      lineHeight: 'normal' // h5 헤더의 줄 간격
    },
    h6: {
      lineHeight: 'normal' // h6 헤더의 줄 간격
    }
  },
  shape: {
    borderRadius: 8 // 기본 테두리 둥글기
  },

  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          lineHeight: 'normal', // 버튼의 기본 줄 간격
          boxShadow: 'none', // 버튼에 그림자 제거
          '&:hover': {
            boxShadow: 'none' // 호버 시 그림자 제거
          },
          '&.Mui-focused': {
            boxShadow: 'none' // 포커스 시 그림자 제거
          },
          '&.MuiButton-sizeLarge': {
            paddingTop: 12, // 큰 버튼 상단 패딩
            paddingBottom: 12, // 큰 버튼 하단 패딩
            paddingLeft: 32, // 큰 버튼 좌측 패딩
            paddingRight: 32, // 큰 버튼 우측 패딩
            fontWeight: 'bold' // 큰 버튼 텍스트 두께
          }
        }
      }
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          height: '32px', // 기본 입력 높이
          borderRadius: '4px', // 입력 필드 테두리 둥글기
          boxSizing: 'border-box', // 박스 모델 설정
          '& .MuiInputBase-input': {
            height: '32px', // 입력 필드 텍스트 높이
            fontSize: '0.75rem', // 폰트 크기
            lineHeight: '1.175rem' // 줄 간격
          }
        }
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          height: '32px', // 기본 아웃라인 입력 높이
          borderRadius: '4px', // 아웃라인 입력 테두리 둥글기
          '& .MuiOutlinedInput-input': {
            height: '32px', // 아웃라인 입력 텍스트 높이
            paddingLeft: '8px', // 아웃라인 입력 좌측 패딩
            paddingRight: '8px', // 아웃라인 입력 우측 패딩
            fontSize: '0.75rem', // 폰트 크기
            boxSizing: 'border-box' // 박스 모델 설정
          }
        }
      }
    },
    MuiDataGrid: {
      styleOverrides: {
        root: {
          '& .MuiDataGrid-columnHeader': {
            backgroundColor: '#f0f0f0' // 그리드 헤더 배경 색상
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: 'bold' // 그리드 헤더 제목 굵게
          },
          '& .MuiDataGrid-cell': {
            fontSize: '0.875rem', // 그리드 셀 텍스트 크기
            lineHeight: '36px', // 셀 줄 간격
            borderRight: '1px solid #E5E5E5' // 셀 오른쪽 테두리
          },
          '& .MuiDataGrid-cell:focus': {
            outline: 'none' // 셀 포커스 시 아웃라인 제거
          }
        }
      }
    }
    // MuiCssBaseline: {
    //   styleOverrides: `
    //     body {
    //       font-family: 'Noto Sans KR', sans-serif; /* 기본 폰트 */
    //     }

    //     /* 일본어의 경우 Meiryo UI 폰트 적용 */
    //     [lang="ja"] body {
    //       font-family: 'Meiryo UI, sans-serif;
    //     }
    //   `
    // }
  }
});

export const lightTheme = createTheme({
  ...baseTheme,
  // 라이트 모드 색상 설정
  colorSchemes: { light: false, dark: false },
  palette: {
    mode: 'light',
    primary: {
      main: '#1976d2' // 라이트 모드의 주요 색상 (파란색)
    },
    contentBlock: {
      main: '#ffffff',
      background: '#f8f8f8'
    },
    fieldText: {
      background: '#ffffff',
      main: '#000000',
      title: '#5d5d5d',
      link: '#4374d9',
      linkHover: '#0D3EA3'
    },
    selectButton: {
      selected: '#4c4c4c',
      unselected: '#ffffff',
      selectedText: '#ffffff',
      unselectedText: '#4c4c4c'
    },
    table: {
      header: '#f0f0f0',
      row: '#ffffff',
      border: '#e0e0e0'
    },
    tiptap: {
      toolbar: '#f0f0f0',
      toolbarText: '#333333',
      main: '#ffffff',
      readonly: '#ffffff',
      readonlyText: '#000000'
    },
    background: {
      default: '#ffffff' // 기본 배경 색상 (흰색)
      // paper: '#f5f5f5' // 종이 배경 색상 (연한 회색)
    },
    text: {
      primary: '#000000', // 텍스트 색상 (검정색)
      secondary: '#757575' // 보조 텍스트 색상 (회색)
    }
  }
  // 컴포넌트 별 스타일 오버라이드 추가
});
export const darkTheme = createTheme({
  ...baseTheme,
  // 다크 모드 색상 설정
  colorSchemes: { light: false, dark: false },
  palette: {
    mode: 'dark', // 다크 모드 설정
    primary: {
      main: '#90caf9', // 다크 모드의 주요 색상 (밝은 파란색)
      dark: '#A6D4FD'
    },
    secondary: {
      main: '#f50057' // 다크 모드의 보조 색상 (밝은 핑크)
    },
    contentBlock: {
      main: '#303030',
      background: '#4c4c4c'
    },
    fieldText: {
      background: '#4c4c4c',
      main: '#ffffff',
      title: '#eaeaea',
      link: '#b2ccff',
      linkHover: '#D6F0FF'
    },
    selectButton: {
      selected: '#303030',
      unselected: '#656565',
      selectedText: '#ffffff',
      unselectedText: '#ffffff'
    },
    table: {
      header: '#303030',
      row: '#404040',
      border: '#252525'
    },
    tiptap: {
      toolbar: '#303030',
      toolbarText: '#cccccc',
      main: '#ffffff',
      readonly: '#303030',
      readonlyText: '#ffffff'
    },
    background: {
      default: '#303030', // 다크 모드 배경 색상 (어두운 회색)
      paper: '#303030' // 다크 모드 종이 배경 색상 (어두운 회색)
    },
    text: {
      primary: '#ffff', // 기본 텍스트 색상 (흰색)
      secondary: '#bdbdbd' // 보조 텍스트 색상 (밝은 회색)
    },
    action: {
      active: '#90caf9', // 다크 모드에서 활성화된 버튼 색상
      hover: '#3f3f3f', // 호버 시 색상
      selected: '#2e2e2e', // 선택된 항목 색상
      disabled: '#616161' // 비활성화된 항목 색상
    },
    // 추가된 색상 옵션들
    error: {
      main: '#f44336' // 에러 상태 색상 (빨간색)
    },
    warning: {
      main: '#ff9800' // 경고 상태 색상 (주황색)
    },
    info: {
      main: '#2196f3' // 정보 상태 색상 (파란색)
    },
    success: {
      main: '#4caf50' // 성공 상태 색상 (초록색)
    },
    divider: '#757575' // 구분선 색상 (어두운 회색)
  },
  // 컴포넌트 별 스타일 오버라이드 추가
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          lineHeight: 'normal', // 버튼의 기본 줄 간격
          boxShadow: 'none', // 버튼에 그림자 제거
          '&:hover': {
            boxShadow: 'none' // 호버 시 그림자 제거
          },
          '&.Mui-focused': {
            boxShadow: 'none' // 포커스 시 그림자 제거
          },
          '&.MuiButton-sizeLarge': {
            paddingTop: 12, // 큰 버튼 상단 패딩
            paddingBottom: 12, // 큰 버튼 하단 패딩
            paddingLeft: 32, // 큰 버튼 좌측 패딩
            paddingRight: 32, // 큰 버튼 우측 패딩
            fontWeight: 'bold' // 큰 버튼 텍스트 두께
          }
        }
      }
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          height: '32px', // 기본 입력 높이
          borderRadius: '4px', // 입력 필드 테두리 둥글기
          boxSizing: 'border-box', // 박스 모델 설정
          '& .MuiInputBase-input': {
            height: '32px', // 입력 필드 텍스트 높이
            fontSize: '0.75rem', // 폰트 크기
            lineHeight: '1.175rem' // 줄 간격
          }
        }
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          height: '32px', // 기본 아웃라인 입력 높이
          borderRadius: '4px', // 아웃라인 입력 테두리 둥글기
          '& .MuiOutlinedInput-input': {
            height: '32px', // 아웃라인 입력 텍스트 높이
            paddingLeft: '8px', // 아웃라인 입력 좌측 패딩
            paddingRight: '8px', // 아웃라인 입력 우측 패딩
            fontSize: '0.75rem', // 폰트 크기
            boxSizing: 'border-box' // 박스 모델 설정
          }
        }
      }
    },
    MuiDataGrid: {
      styleOverrides: {
        root: {
          '& .MuiDataGrid-columnHeader': {
            backgroundColor: '#000000' // 그리드 헤더 배경 색상
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: 'bold' // 그리드 헤더 제목 굵게
          },
          '& .MuiDataGrid-cell': {
            fontSize: '0.875rem', // 그리드 셀 텍스트 크기
            lineHeight: '36px', // 셀 줄 간격
            borderRight: '1px solid #E5E5E5' // 셀 오른쪽 테두리
          },
          '& .MuiDataGrid-cell:focus': {
            outline: 'none' // 셀 포커스 시 아웃라인 제거
          }
        }
      }
    },
    MuiCssBaseline: {
      styleOverrides: `
      body {
        font-family: 'Noto Sans KR', sans-serif; /* 기본 폰트 */
      }

      /* 일본어의 경우 Meiryo UI 폰트 적용 */
      [lang="ja"] body {
        font-family: Meiryo UI, sans-serif;
      }
      
      [lang="ja"] p {
        line-height: 1.2;
      }
    `
    }
  }
});
export const customAppTheme = { theme: baseTheme, light: lightTheme, dark: darkTheme };
